// 封装网络请求
import axios from "axios";
import { clearAuthStorage, redirectToLogin } from './auth';

// 根据环境变量设置基础URL
const baseURL = process.env.VUE_APP_BASE_API || 'https://nadc.china-vo.org/galaxycircus';
const isDevMode = process.env.VUE_APP_DEV_MODE === 'true';
const autoLogin = process.env.VUE_APP_AUTO_LOGIN === 'true';

const ajax = axios.create({
  baseURL: baseURL,
  timeout: 10000,
})

function isUnauthorizedBusinessCode(response) {
  const code = response?.data?.code;
  return code === 401 || code === '401';
}

function buildUnauthorizedError(response) {
  const error = new Error(response?.data?.message || '认证信息无效或已过期');
  error.name = 'UnauthorizedError';
  error.response = response;
  error.config = response?.config;
  return error;
}

// 开发环境自动获取Token
let devTokenPromise = null;

async function getDevToken() {
  if (!isDevMode || !autoLogin) return null;
  
  // 如果已经有Token，直接返回
  const existingToken = localStorage.getItem('Token-Circus');
  if (existingToken) return existingToken;
  
  // 避免重复请求
  if (devTokenPromise) return devTokenPromise;
  
  devTokenPromise = axios.get(`${baseURL}/api/dev/mock-login`)
    .then(response => {
      const data = response.data;
      if (data.success && data.token) {
        localStorage.setItem('Token-Circus', data.token);
        localStorage.setItem('umtId', data.umtId);
        console.log('[开发模式] 自动登录成功:', data.name);
        return data.token;
      }
      return null;
    })
    .catch(error => {
      console.warn('[开发模式] 自动登录失败:', error.message);
      return null;
    })
    .finally(() => {
      devTokenPromise = null;
    });
  
  return devTokenPromise;
}

async function retryRequestWithDevLogin(config) {
  if (!isDevMode || !autoLogin || !config || config._devAuthRetried) {
    return null;
  }

  console.log('[开发模式] Token失效，重新获取...');
  clearAuthStorage();

  const token = await getDevToken();
  if (!token) {
    return null;
  }

  const retryConfig = {
    ...config,
    _devAuthRetried: true,
    headers: {
      ...(config.headers || {}),
      'Token-Circus': token,
    }
  };

  const umtId = localStorage.getItem('umtId');
  if (umtId) {
    retryConfig.headers['Unique-Ident'] = umtId;
  }

  return ajax.request(retryConfig);
}

// 请求拦截器
ajax.interceptors.request.use(
  async config => {
    config.headers = config.headers || {};

    // 所有环境：优先携带本地认证信息
    const localToken = localStorage.getItem('Token-Circus');
    const localUmtId = localStorage.getItem('umtId');
    if (localToken && !config.headers['Token-Circus']) {
      config.headers['Token-Circus'] = localToken;
    }
    if (localUmtId && !config.headers['Unique-Ident']) {
      config.headers['Unique-Ident'] = localUmtId;
    }

    // 开发环境：如果缺少认证信息，自动登录补齐
    if (isDevMode && autoLogin && (!config.headers['Token-Circus'] || !config.headers['Unique-Ident'])) {
      const token = await getDevToken();
      const umtId = localStorage.getItem('umtId');
      if (token && !config.headers['Token-Circus']) {
        config.headers['Token-Circus'] = token;
      }
      if (umtId && !config.headers['Unique-Ident']) {
        config.headers['Unique-Ident'] = umtId;
      }
    }
    
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// 响应拦截器
ajax.interceptors.response.use(
  async response => {
    if (isUnauthorizedBusinessCode(response)) {
      const retryResponse = await retryRequestWithDevLogin(response.config);
      if (retryResponse) {
        return retryResponse;
      }

      if (!isDevMode || !autoLogin) {
        redirectToLogin();
      }

      return Promise.reject(buildUnauthorizedError(response));
    }

    return response;
  },
  async error => {
    // 开发环境下，如果401错误，尝试重新获取Token
    if (error.response?.status === 401) {
      const retryResponse = await retryRequestWithDevLogin(error.config);
      if (retryResponse) {
        return retryResponse;
      }

      if (!isDevMode || !autoLogin) {
        redirectToLogin();
      }
    }
    
    return Promise.reject(error);
  }
);

// 对外导出
export default ajax
